import * as React from 'react';
import { useFormState, type FieldValues, type UseFormReturn } from 'react-hook-form';

interface FormDraft<V, E = undefined> {
  values: V;
  extra?: E;
}

// clearDraft opens a short suppression window so the navigation/reset that
// follows a successful create can't immediately resurrect the draft. Module
// scope (keyed by storage key) on purpose: navigate(-1) back to the same
// create route remounts the page, and a ref would start the new instance at 0.
const suppressUntil = new Map<string, number>();

/**
 * Read a previously saved form draft synchronously — meant for `useForm`
 * defaultValues / `useState` initializers so the draft is applied before the
 * first render (no reset dance, no effects racing). Returns null when there is
 * no draft or it can't be parsed.
 *
 * Note: values come back from JSON, so non-JSON field types (e.g. Date) must
 * be revived by the caller.
 */
export function readFormDraft<V, E = undefined>(key: string): FormDraft<V, E> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const draft = JSON.parse(raw) as FormDraft<V, E>;
    return draft?.values ? draft : null;
  } catch {
    return null;
  }
}

/**
 * Persist a create-form's in-progress values to localStorage (debounced) so an
 * accidental refresh/navigation doesn't lose the user's input. Call
 * `clearDraft()` after the entity is successfully created.
 *
 * Saves only once the user actually changed something (form `isDirty`, or the
 * `extra` state changed) — react-hook-form fires watch events for mount-time
 * field registration too, and without the gate every visit to a create page
 * would write a junk draft of default values.
 *
 * @param enabled  save only when true (pass `!isEditMode` — drafts are for
 *                 create forms, never for editing existing data)
 * @param extra    optional non-RHF state to persist alongside the form values
 *                 (must be referentially stable between renders — memoize it)
 */
export function useFormDraft<T extends FieldValues, E = undefined>({
  key,
  form,
  enabled,
  extra,
}: {
  key: string;
  form: UseFormReturn<T>;
  enabled: boolean;
  extra?: E;
}) {
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const extraRef = React.useRef(extra);
  extraRef.current = extra;

  // Subscribed via useFormState so the value is live; mirrored into a ref so
  // the watch callback reads the current value without re-subscribing.
  // dirtyFields (not the global isDirty) on purpose: isDirty deep-compares
  // against defaultValues, so mount-time programmatic seeding (e.g. the hourly
  // package's fixed rate rows) flips it and every visit would write a junk
  // draft. dirtyFields only records user-driven changes (field.onChange /
  // setValue with shouldDirty).
  const { dirtyFields } = useFormState({ control: form.control });
  const isDirtyRef = React.useRef(false);
  isDirtyRef.current = Object.keys(dirtyFields).length > 0;
  const extraDirtyRef = React.useRef(false);

  const save = React.useCallback(
    (values: unknown) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Gates are checked HERE, not at event time: the watch callback fires
        // synchronously before React re-renders, so on the very first user
        // change isDirtyRef still holds the pre-change value.
        if (!isDirtyRef.current && !extraDirtyRef.current) return;
        if (Date.now() < (suppressUntil.get(key) ?? 0)) return;
        try {
          localStorage.setItem(key, JSON.stringify({ values, extra: extraRef.current }));
        } catch {
          // localStorage unavailable/full — the draft is best-effort only
        }
      }, 500);
    },
    [key],
  );

  React.useEffect(() => {
    if (!enabled) return;
    const subscription = form.watch((values) => {
      save(values);
    });
    return () => {
      subscription.unsubscribe();
      clearTimeout(timerRef.current);
    };
  }, [enabled, form, save]);

  // Extra state lives outside react-hook-form, so its changes don't fire the
  // watch subscription — persist them explicitly. Change detection compares
  // CONTENT (not effect runs or object identity): StrictMode double-runs
  // mount effects and re-creates the caller's useMemo object, and a
  // "skip the first run" flag misread that as a user change, writing a junk
  // draft of defaults on every page load.
  const extraJson = extra === undefined ? '' : JSON.stringify(extra);
  const prevExtraJsonRef = React.useRef(extraJson);
  React.useEffect(() => {
    if (!enabled) return;
    if (prevExtraJsonRef.current === extraJson) return;
    prevExtraJsonRef.current = extraJson;
    extraDirtyRef.current = true;
    save(form.getValues());
  }, [enabled, extraJson, form, save]);

  const clearDraft = React.useCallback(() => {
    clearTimeout(timerRef.current);
    suppressUntil.set(key, Date.now() + 1500);
    extraDirtyRef.current = false;
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore — nothing to clean up if storage is unavailable
    }
  }, [key]);

  return { clearDraft };
}
